import type { LumaSubcategory, LumaSize, LumaOptionItem, LumaOptionGroup } from './types';

export const GLAZING_GROUP = 'Glazing';
export const MAT_SIZE_GROUP = 'Mat Size';

/**
 * Parse mat border width (inches per side) from a Mat Size option label.
 */
export function parseMatInset(optionName: string): number {
	if (/no mat/i.test(optionName)) {
		return 0;
	}

	const match = optionName.match(/([\d.]+)\s*inch/i);
	return match ? Math.max(0, parseFloat(match[1])) : 0;
}

/**
 * True when the glazing option includes glass (not "No Glass").
 */
export function isGlazingWithGlass(optionName: string): boolean {
	return !/no\s*glass/i.test(optionName);
}

/**
 * Whether glass dimensions fit max glass in either orientation.
 */
export function glassFits(
	glassW: number,
	glassH: number,
	maxGlassWidth: number,
	maxGlassHeight: number,
): boolean {
	if (glassW <= 0 || glassH <= 0) {
		return false;
	}

	const fitsNatural =
		glassW <= maxGlassWidth && glassH <= maxGlassHeight;
	const fitsFlipped =
		glassH <= maxGlassWidth && glassW <= maxGlassHeight;

	return fitsNatural || fitsFlipped;
}

/**
 * Whether a print size with mat inset fits the subcategory max glass limits.
 */
export function printFitsWithMat(
	printW: number,
	printH: number,
	matInset: number,
	maxGlassWidth: number,
	maxGlassHeight: number,
): boolean {
	const glassW = printW + 2 * matInset;
	const glassH = printH + 2 * matInset;

	return glassFits(glassW, glassH, maxGlassWidth, maxGlassHeight);
}

/**
 * Resolve the selected glazing option name from visible groups or auto-assigned options.
 */
export function getGlazingOptionName(
	subcat: LumaSubcategory,
	selectedOptions: Record<string, number>,
): string | null {
	const glazingId = selectedOptions[GLAZING_GROUP];

	if (glazingId === undefined) {
		return null;
	}

	const glazingGroup = subcat.optionGroups.find((g) => g.groupName === GLAZING_GROUP);
	const visibleItem = glazingGroup?.items.find((i) => i.optionId === glazingId);

	if (visibleItem) {
		return visibleItem.optionName;
	}

	const auto = subcat.autoAssignedOptions[GLAZING_GROUP];

	if (auto?.optionId === glazingId) {
		return auto.optionName;
	}

	return null;
}

/**
 * Filter Mat Size options: apply glass cap only when glazing includes glass.
 */
export function filterMatItemsForGlass(
	size: LumaSize,
	items: LumaOptionItem[],
	subcat: LumaSubcategory,
	selectedOptions: Record<string, number>,
): LumaOptionItem[] {
	if (!subcat.usesGlassMatRules || subcat.maxGlassWidth == null || subcat.maxGlassHeight == null) {
		return items;
	}

	const glazingName = getGlazingOptionName(subcat, selectedOptions);

	if (glazingName === null || !isGlazingWithGlass(glazingName)) {
		return items;
	}

	return items.filter((item) =>
		printFitsWithMat(
			size.width,
			size.height,
			parseMatInset(item.optionName),
			subcat.maxGlassWidth!,
			subcat.maxGlassHeight!,
		),
	);
}

/**
 * Put Glazing before Mat Size; keep other groups in their original order.
 */
export function sortOptionGroupsForDisplay(groups: LumaOptionGroup[]): LumaOptionGroup[] {
	const glazing = groups.find((g) => g.groupName === GLAZING_GROUP);
	const matSize = groups.find((g) => g.groupName === MAT_SIZE_GROUP);
	const rest = groups.filter(
		(g) => g.groupName !== GLAZING_GROUP && g.groupName !== MAT_SIZE_GROUP,
	);

	const sorted: LumaOptionGroup[] = [...rest];

	if (glazing) {
		sorted.push(glazing);
	}

	if (matSize) {
		sorted.push(matSize);
	}

	return sorted;
}
